import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, TrendingUp, TrendingDown, Receipt } from "lucide-react";
import { brcoinService, BRCoinTransacao } from "../../services/brcoinService";
import { enqueueSnackbar } from "notistack";

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function Extrato() {
  const navigate = useNavigate();
  const [transacoes, setTransacoes] = useState<BRCoinTransacao[]>([]);
  const [saldo, setSaldo] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [extrato, saldoData] = await Promise.all([
          brcoinService.getExtrato(),
          brcoinService.getSaldo(),
        ]);
        setTransacoes(extrato);
        setSaldo(saldoData.brcoins);
      } catch {
        enqueueSnackbar("Erro ao carregar extrato", { variant: "error" });
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const totalCreditos = transacoes
    .filter((t) => t.tipo === "credito")
    .reduce((sum, t) => sum + t.valor, 0);

  const totalDebitos = transacoes
    .filter((t) => t.tipo === "debito")
    .reduce((sum, t) => sum + t.valor, 0);

  return (
    <div className="container mx-auto px-4 py-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="p-2 text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Receipt className="w-6 h-6 text-yellow-500" />
            Extrato de BRCoins
          </h1>
          <p className="text-sm text-muted-foreground">
            Histórico completo de ganhos e gastos
          </p>
        </div>
      </div>

      {/* Cards resumo */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div
          className="p-4 rounded-xl border"
          style={{
            background: "oklch(0.85 0.12 85 / 0.10)",
            borderColor: "oklch(0.75 0.15 85 / 0.3)",
          }}
        >
          <p className="text-xs text-muted-foreground mb-1">Saldo atual</p>
          <p
            className="text-xl font-bold"
            style={{ color: "oklch(0.50 0.15 85)" }}
          >
            ₿ {saldo.toLocaleString("pt-BR")}
          </p>
        </div>
        <div
          className="p-4 rounded-xl border"
          style={{
            background: "oklch(0.85 0.15 142 / 0.10)",
            borderColor: "oklch(0.65 0.15 142 / 0.3)",
          }}
        >
          <p className="text-xs text-muted-foreground mb-1">Total ganhos</p>
          <p className="text-xl font-bold text-green-600">
            +{totalCreditos.toLocaleString("pt-BR")}
          </p>
        </div>
        <div
          className="p-4 rounded-xl border"
          style={{
            background: "oklch(0.85 0.15 25 / 0.10)",
            borderColor: "oklch(0.65 0.15 25 / 0.3)",
          }}
        >
          <p className="text-xs text-muted-foreground mb-1">Total gastos</p>
          <p className="text-xl font-bold text-red-500">
            -{totalDebitos.toLocaleString("pt-BR")}
          </p>
        </div>
      </div>

      {/* Lista de transações */}
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : transacoes.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <Receipt className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>Nenhuma transação registrada ainda.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {transacoes.map((transacao) => (
            <div
              key={transacao.id}
              className="flex items-center justify-between p-4 rounded-xl border border-border"
              style={{ background: "oklch(var(--card))" }}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`p-2 rounded-full ${
                    transacao.tipo === "credito"
                      ? "bg-green-100 dark:bg-green-900/30"
                      : "bg-red-100 dark:bg-red-900/30"
                  }`}
                >
                  {transacao.tipo === "credito" ? (
                    <TrendingUp className="w-4 h-4 text-green-600" />
                  ) : (
                    <TrendingDown className="w-4 h-4 text-red-500" />
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {transacao.descricao}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatDate(transacao.criado_em)}
                  </p>
                </div>
              </div>
              <span
                className={`text-sm font-bold ${
                  transacao.tipo === "credito"
                    ? "text-green-600"
                    : "text-red-500"
                }`}
              >
                {transacao.tipo === "credito" ? "+" : "-"}
                {transacao.valor.toLocaleString("pt-BR")}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
